/**
 * src/pages/Dashboard.jsx — the page an artist opens first each morning.
 *
 * Answers three questions and nothing else: who is coming in today, what got
 * booked recently, and whose paperwork is still outstanding. Anything else
 * belongs on the page that owns it.
 */
import { Link } from 'react-router-dom';

import { useApi } from '../lib/useApi.js';
import { formatDateTime, formatTime } from '../lib/format.js';
import { zonedParts, zonedTimeToUnix } from '../lib/timezone.js';
import { Empty, Loader, PageHeader, Pill } from '../components/ui.jsx';
import { useAuth } from '../lib/auth.jsx';

/**
 * Start of today in a given zone, as unix seconds.
 *
 * In the studio's zone, not the viewer's — an artist checking their phone
 * abroad should see the same "today" as a colleague at the studio.
 *
 * @param {string} timeZone
 * @returns {number}
 */
function startOfToday(timeZone) {
  const p = zonedParts(Math.floor(Date.now() / 1000), timeZone);
  return zonedTimeToUnix(
    `${p.year}-${String(p.month).padStart(2, '0')}-${String(p.day).padStart(2, '0')}`,
    '00:00',
    timeZone,
  );
}

export default function Dashboard() {
  const { artist, timezone } = useAuth();
  const dayStart = startOfToday(timezone);
  const dayEnd = dayStart + 86400;

  const today = useApi(`/api/appointments?from=${dayStart}&to=${dayEnd}`);
  // Everything still ahead, newest booking first, for the "recent" panel.
  const upcoming = useApi(`/api/appointments?from=${dayEnd}`);
  const consent = useApi('/api/consent?status=pending');

  const todays = (today.data?.appointments ?? []).filter((a) => a.status !== 'cancelled');

  return (
    <>
      <PageHeader
        title={`Good ${greeting(timezone)}, ${artist?.name?.split(' ')[0] ?? 'there'}`}
        subtitle={new Date().toLocaleDateString(undefined, {
          weekday: 'long',
          day: 'numeric',
          month: 'long',
          timeZone: timezone,
        })}
      />

      <div className="stack-lg">
        <section className="card">
          <div className="row-between" style={{ marginBottom: 14 }}>
            <h2>Today · {todays.length} {todays.length === 1 ? 'appointment' : 'appointments'}</h2>
            <Link to="/calendar" className="btn btn-sm">Open calendar</Link>
          </div>

          <Loader loading={today.loading} error={today.error}>
            {todays.length === 0 ? (
              <Empty>Nothing booked today.</Empty>
            ) : (
              <div className="table-wrap">
                <table className="table">
                  <thead>
                    <tr>
                      <th>Time</th>
                      <th>Client</th>
                      <th>Artist</th>
                      <th>Project</th>
                      <th>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {todays.map((a) => (
                      <tr key={a.id}>
                        <td className="mono">
                          {formatTime(a.startsAt, timezone)} – {formatTime(a.endsAt, timezone)}
                        </td>
                        <td>
                          <Link to={`/clients/${a.clientId}`}>{a.clientName}</Link>
                        </td>
                        <td className="muted">{a.artistName}</td>
                        <td className="muted">{a.projectTitle ?? '—'}</td>
                        <td><Pill status={a.status} /></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Loader>
        </section>

        <div className="row wrap" style={{ alignItems: 'flex-start', gap: 24 }}>
          <section className="card grow" style={{ minWidth: 320 }}>
            <h2 style={{ marginBottom: 14 }}>Coming up</h2>
            <Loader loading={upcoming.loading} error={upcoming.error}>
              {(upcoming.data?.appointments ?? []).length === 0 ? (
                <Empty>No future bookings yet.</Empty>
              ) : (
                <div className="stack">
                  {(upcoming.data?.appointments ?? []).slice(0, 6).map((a) => (
                    <div key={a.id} className="row-between small">
                      <span>
                        <strong>{a.clientName}</strong>
                        <span className="muted"> · {a.artistName}</span>
                      </span>
                      <span className="muted mono">{formatDateTime(a.startsAt, timezone)}</span>
                    </div>
                  ))}
                </div>
              )}
            </Loader>
          </section>

          <section className="card grow" style={{ minWidth: 320 }}>
            <div className="row-between" style={{ marginBottom: 14 }}>
              <h2>Consent outstanding</h2>
              <Link to="/consent" className="btn btn-sm">Manage</Link>
            </div>
            <Loader loading={consent.loading} error={consent.error}>
              {(consent.data?.forms ?? []).length === 0 ? (
                <Empty>Everyone&rsquo;s paperwork is done.</Empty>
              ) : (
                <div className="stack">
                  {(consent.data?.forms ?? []).slice(0, 6).map((f) => (
                    <div key={f.id} className="row-between small">
                      <span>
                        <strong>{f.clientName}</strong>
                        <span className="muted"> · {f.artistName}</span>
                      </span>
                      <span className="muted mono">{formatDateTime(f.appointmentStartsAt, timezone)}</span>
                    </div>
                  ))}
                </div>
              )}
            </Loader>
          </section>
        </div>
      </div>
    </>
  );
}

/**
 * Time-of-day greeting, in the studio's own hour — not the viewer's.
 *
 * @param {string} timeZone
 * @returns {'morning'|'afternoon'|'evening'}
 */
function greeting(timeZone) {
  const hour = zonedParts(Math.floor(Date.now() / 1000), timeZone).hour;
  if (hour < 12) return 'morning';
  if (hour < 18) return 'afternoon';
  return 'evening';
}
